import { useMemo } from 'react';

import type { WorkflowPhaseDef, WorkflowView } from '@/lib/api/generated/model';
import { useWorkspace } from '@/features/workspaces';

/**
 * Steps live inside phases in the contract; the synthesized start and done
 * steps live outside them (`start_step` / `done_step`), so a sum over phases
 * counts exactly the author-declared steps — the number a list row shows.
 */
export function countDeclaredSteps(phases: WorkflowPhaseDef[]): number {
  return phases.reduce(
    (total, phase) =>
      total +
      (phase.steps ?? []).filter(
        (step) => step.type !== 'backlog' && step.type !== 'done',
      ).length,
    0,
  );
}

/**
 * Board order: oldest workflow first (intent 002 BR-002-04).
 *
 * A plain function, not a hook, because the boards route resolves its default
 * board in a loader — which cannot call hooks — and that choice must agree with
 * what the navigation lists. Two copies of this comparison would mean the board
 * the nav shows first is not necessarily the board `/boards` opens.
 *
 * `display_order` leads only to leave room for an explicit ordering later:
 * nothing assigns it today, so every workflow carries the column default of 0
 * and `created_at` is what actually orders the list. `key` breaks remaining
 * ties because it is stable, where a name can be changed by a rename.
 */
export function sortWorkflowsForBoards(list: WorkflowView[]): WorkflowView[] {
  return [...list].sort(
    (a, b) =>
      a.display_order - b.display_order ||
      // Coalesced rather than read directly: `created_at` is a recent addition
      // to the contract, and an older or partial payload that omits it should
      // fall through to the stable `key` tiebreaker instead of throwing on
      // `undefined.localeCompare`.
      (a.created_at ?? '').localeCompare(b.created_at ?? '') ||
      a.key.localeCompare(b.key),
  );
}

/**
 * The workflows a board may be built from: published ones only (FR-004).
 *
 * A draft has no committed definition, so it has no columns and cannot hold a
 * task. Offering a board for one would be offering a surface with nothing on it
 * and no way to put anything there.
 *
 * **Deliberately a separate function rather than a filter inside
 * `sortWorkflowsForBoards` or `useWorkflows`.** Both of those are consumed by
 * the workflow *list* and by `useWorkflow`, and filtering there would make a
 * draft invisible in the list it must appear in (FR-003) and 404 the builder on
 * the very draft it had just created (FR-005). The board surfaces are the ones
 * that need the narrower set, so they are the ones that ask for it.
 */
export function boardEligibleWorkflows(list: WorkflowView[]): WorkflowView[] {
  return sortWorkflowsForBoards(list).filter(
    (workflow) => workflow.status === 'published',
  );
}

/**
 * The workflows of one workspace, in list order.
 *
 * Derived from the workspace aggregate read — the one server query this
 * feature has (spec D-10). There is deliberately no workflows-specific query:
 * a second cache entry for the same server object is how the list and the
 * graph would drift apart.
 *
 * The three states a caller renders are disjoint by construction:
 * `isPending` while the read is in flight, `loadFailed`, and otherwise
 * `workflows`, possibly empty. Empty-after-load is the real empty state; it is
 * never conflated with failure (FR-007).
 *
 * Failure has two shapes and both must count. A response that arrives with a
 * non-200 status resolves to `null` (see the aggregate hook's own comment); a
 * request that never gets a response at all — backend down, DNS failure,
 * offline — throws, leaving `data` undefined and only `isError` set. Treating
 * the second as an empty list is the bug this condition exists to prevent: it
 * invites the user to add a first workflow to a workspace that already has
 * some, and tells a deep-link recipient the workflow was deleted when the
 * server is merely unreachable.
 */
export function useWorkflows(workspaceId: string) {
  const query = useWorkspace(workspaceId);

  const workflows: WorkflowView[] = useMemo(
    () => sortWorkflowsForBoards(query.data?.workflows ?? []),
    [query.data],
  );

  return {
    workflows,
    isPending: query.isPending,
    loadFailed: !query.isPending && (query.isError || query.data === null),
    refetch: query.refetch,
  };
}
